/**
 * What the same money would have done in gold.
 *
 * The value band answers "what has this car been worth". A reader's next
 * question is always "compared to what", and inflation — which is what the
 * badge is graded against — is an abstraction nobody has ever bought. Gold is
 * the concrete version of the same question: the thing somebody in 1987 could
 * have put the price of an F40 into instead, and the one alternative store of
 * value that needs no explaining.
 *
 * The series below is the ANNUAL AVERAGE London price in US dollars per troy
 * ounce, which is the same basis for every year and the same currency as every
 * price in the catalogue. One basis, stated once, for the reason `carPrices.js`
 * gives about mixing an average with a hammer price: a benchmark drawn from two
 * kinds of number is a benchmark that measures nothing.
 *
 * ## Why this is in the domain and not in the fetched catalogue
 *
 * It is a YARDSTICK, not content. `ANNUAL_INFLATION` in `carValue.js` makes the
 * same argument in one number: it is not a fact about a car, it is the thing
 * every car is measured against, and a per-car copy of a yardstick is how two
 * cars end up measured differently. Putting it on the records would write the
 * same forty numbers onto every one of them.
 *
 * The catalogue's own reason for being fetched does not apply either. Car prose
 * is out of the bundle because a visitor who opens one car should not pay to
 * parse two dozen descriptions; this is forty-three integers, and it is read by
 * every card rather than by one.
 *
 * ## What ages, and what does not
 *
 * Only the last row. 1995 will not be revalued. `THROUGH` names the year the
 * series runs to, and it has to match the year `carPrices.AS_OF` stamps —
 * `goldBenchmark.test.js` holds the two together, because a reference line read
 * in a different year from the market figures it is drawn against would move
 * the crossover point and nothing on screen would say so.
 *
 * The final row is a year-to-date average rather than a settled one, and it is
 * the least certain figure in this module by a distance: gold moved further in
 * 2025 than in any year since 1979. It is refreshed with `AS_OF`, in the same
 * commit, from one reading.
 */

/** The last year the series covers. Moves with `carPrices.AS_OF`, never alone. */
export const THROUGH = 2026;

/**
 * US dollars per troy ounce, annual average, London.
 *
 * Starts at 1984 because that is the earliest year any car in the catalogue was
 * built in, and a benchmark that could not reach a car's launch year would draw
 * that one card without a reference line for no reason a reader could see.
 */
const GOLD_USD_PER_OUNCE = {
    1984: 360,
    1985: 317,
    1986: 368,
    1987: 447,
    1988: 437,
    1989: 381,
    1990: 384,
    1991: 362,
    1992: 344,
    1993: 360,
    1994: 384,
    1995: 384,
    1996: 388,
    1997: 331,
    1998: 294,
    1999: 279,
    2000: 279,
    2001: 271,
    2002: 310,
    2003: 363,
    2004: 409,
    2005: 445,
    2006: 604,
    2007: 695,
    2008: 872,
    2009: 972,
    2010: 1225,
    2011: 1572,
    2012: 1669,
    2013: 1411,
    2014: 1266,
    2015: 1160,
    2016: 1251,
    2017: 1257,
    2018: 1269,
    2019: 1393,
    2020: 1770,
    2021: 1799,
    2022: 1801,
    2023: 1941,
    // The two that move. 2025 is the year gold ran; 2026 is a year-to-date
    // average and is the row to correct when `AS_OF` next moves.
    2024: 2386,
    2025: 3250,
    2026: 3800,
};

/** The price in `year`, or null for a year outside the series. */
export function goldPrice(year) {
    return GOLD_USD_PER_OUNCE[year] ?? null;
}

/**
 * The same money, put into gold in `from` and held.
 *
 * One point per YEAR rather than one per point of the car's own series, and
 * that is the whole value of drawing it: the car has four to six anchors and
 * gold has an annual close, so the reference line carries its own shape — the
 * 2011 peak, the flat middle of the nineties — instead of being straightened to
 * match the thing it is being compared with.
 *
 * Scaled to the car's launch price rather than drawn in dollars per ounce, so
 * the two lines share an axis and the comparison needs no second scale to read.
 * Both start from the same point by construction: at `from` the ratio is one.
 *
 * Returns null rather than an empty list for a span the series does not cover,
 * so a caller that forgets to check draws no reference rather than a flat line
 * along the bottom of the plot.
 */
export function goldBenchmark({ from, to, amount } = {}) {
    const base = goldPrice(from);

    if (!base || !Number.isFinite(amount) || amount <= 0 || !goldPrice(to) || to <= from) {
        return null;
    }

    const points = [];

    for (let year = from; year <= to; year += 1) {
        const price = goldPrice(year);

        if (price) {
            points.push({ year, amount: (amount * price) / base });
        }
    }

    return points;
}

/**
 * What that holding would be worth at the end of the span.
 *
 * Returned separately from the line because the caption prints it in words, and
 * it has to be right even when the line itself is not fully visible: the plot is
 * scaled to the CAR, so for a car gold beat by a wide margin the reference
 * leaves the top of the frame. The sentence is what carries the figure then, and
 * it is the reason the band never depends on the line alone.
 */
export function goldEquivalent({ from, to, amount } = {}) {
    const points = goldBenchmark({ from, to, amount });

    return points ? points[points.length - 1].amount : null;
}
